import { useMemo } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import type { DateTimeRange } from '#components/shared/datePeriod.ts';
import { matchesOrderTimeRange } from '#components/tour/CreateTourDialog/orderFilters.ts';
import { containsOrderSearchTerm } from '#utils/orderSearch.ts';
import type { OrderWithSortableKey } from '#utils/orders.util.ts';

const containsFilterTerm = (item: OrderWithSortableKey, statusFilter: string[]) => {
    return !item.status || statusFilter.length === 0 || statusFilter.includes(item.status);
};

const matchesFilters = (item: OrderWithSortableKey, statusFilter: string[], search: string, intl: IntlShape) =>
    containsFilterTerm(item, statusFilter) && containsOrderSearchTerm(item, search, intl);

export const useFilteredOrders = (
    items: OrderWithSortableKey[],
    search: string,
    statusFilter: string[],
    selectedRowIds: string[],
    selectedOrderId?: string,
    range?: DateTimeRange
) => {
    const intl = useIntl();
    const filteredOrders = useMemo(
        () =>
            items.filter(
                item => matchesFilters(item, statusFilter, search, intl) && matchesOrderTimeRange(item, range)
            ),
        [items, search, statusFilter, intl, range?.start, range?.end]
    );
    const selectedOrders = useMemo(
        () => filteredOrders.filter(order => selectedRowIds.includes(order.id)),
        [filteredOrders, selectedRowIds]
    );
    const selectableOrders = useMemo(() => filteredOrders.filter(order => order.status === 'open'), [filteredOrders]);
    const activeOrder = useMemo(() => items.find(order => order.id === selectedOrderId), [items, selectedOrderId]);

    return {
        filteredOrders,
        selectedOrders,
        selectableOrders,
        activeOrder,
    };
};
